import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiClock, FiZap, FiShield, FiAward, FiDollarSign } = FiIcons;

const OfferSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 3,
    hours: 12,
    minutes: 45,
    seconds: 30
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const packages = [
    {
      name: 'Essential',
      setup: '$1,000',
      monthly: '$0 (DIY)',
      bestFor: 'Coaches comfortable managing the AI themselves after setup',
      features: [
        'Custom AI Assistant Build',
        'Initial Training & Setup',
        'Calendar Integration',
        'Basic Analytics Dashboard'
      ],
      popular: false
    },
    {
      name: 'Professional',
      setup: '$1,200',
      setupDiscounted: '$800',
      monthly: '$200/month',
      bestFor: 'Coaches who want ongoing optimization and priority support',
      features: [
        'Everything in Essential',
        'Ongoing Optimization',
        'Priority Support',
        'Monthly Performance Reports',
        'CRM Integration'
      ],
      popular: true,
      savings: '$400 OFF'
    },
    {
      name: 'Premium',
      setup: '$1,500',
      monthly: '$300/month',
      bestFor: 'Consultants who want complete white-glove service',
      features: [
        'Everything in Professional',
        'Advanced Integrations',
        'Strategy Consultation',
        'Quarterly AI Refinement',
        'Dedicated Account Manager'
      ],
      popular: false
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-6 py-3 mb-6 shadow-xl"
            >
              <SafeIcon icon={FiZap} className="text-yellow-300 text-xl" />
              <span className="font-bold">🔥 BLACK FRIDAY SPECIAL - ENDS NOVEMBER 15TH</span>
            </motion.div>

            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              An Investment That Pays for Itself in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Days, Not Months</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              Hiring a human assistant costs $3,000-$5,000 per month. Custom AI development costs $50,000+. 
              For a limited time, you can get a complete, Done-For-You AI solution that delivers better results for a fraction of the price.
            </p>
          </div>

          {/* Countdown Timer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-8 rounded-2xl mb-12 shadow-2xl max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <SafeIcon icon={FiClock} className="text-4xl animate-pulse" />
              <span className="text-3xl font-bold">Black Friday Offer Ends In:</span>
            </div>
            
            <div className="flex justify-center gap-4 md:gap-8">
              {Object.entries(timeLeft).map(([unit, value]) => (
                <div key={unit} className="text-center">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] shadow-lg">
                    <div className="text-4xl md:text-6xl font-bold">{value.toString().padStart(2, '0')}</div>
                  </div>
                  <div className="text-base md:text-lg mt-2 capitalize font-semibold">{unit}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Pricing Packages */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`bg-white rounded-3xl shadow-xl p-8 relative ${
                  pkg.popular ? 'border-4 border-orange-400 scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-full font-bold text-sm shadow-lg">
                    ⭐ MOST POPULAR
                  </div>
                )}

                {pkg.savings && (
                  <div className="absolute -top-4 -right-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg rotate-12">
                    {pkg.savings}
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                  
                  <div className="mb-4">
                    {pkg.setupDiscounted ? (
                      <>
                        <p className="text-gray-500 line-through text-lg mb-1">{pkg.setup}</p>
                        <p className="text-5xl font-bold text-orange-600 mb-2">{pkg.setupDiscounted}</p>
                      </>
                    ) : (
                      <p className="text-5xl font-bold text-gray-900 mb-2">{pkg.setup}</p>
                    )}
                    <p className="text-sm text-gray-600">Setup Fee</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3 mb-4">
                    <p className="text-lg font-bold text-blue-800">{pkg.monthly}</p>
                    <p className="text-xs text-blue-600">Monthly Management</p>
                  </div>

                  <p className="text-sm text-gray-600 mb-6">{pkg.bestFor}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:shadow-xl'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {pkg.popular ? 'CLAIM YOUR $400 DISCOUNT' : 'Select Package'}
                </motion.button>
              </motion.div>
            ))}
          </div>

          {/* Black Friday Bonus */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-10 rounded-3xl shadow-2xl text-center"
          >
            <SafeIcon icon={FiAward} className="text-6xl mx-auto mb-6" />
            <h3 className="text-4xl font-bold mb-6">Black Friday Bonus</h3>
            <p className="text-2xl mb-8">Sign up before November 15th and get:</p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <SafeIcon icon={FiDollarSign} className="text-4xl mb-3 mx-auto" />
                <p className="text-2xl font-bold mb-2">$400 OFF</p>
                <p className="text-purple-200">Your setup fee</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <SafeIcon icon={FiShield} className="text-4xl mb-3 mx-auto" />
                <p className="text-2xl font-bold mb-2">3 Months FREE</p>
                <p className="text-purple-200">Noem's Enterprise Plan ($297 value)</p>
              </div>
            </div>

            <p className="text-xl font-bold mb-4">Total Bonus Value: $697</p>
            <p className="text-purple-200">This offer is only available to the first 50 coaches & consultants</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default OfferSection;